/**
 * pin-modal.ts — PIN girişi için modal
 * Basit ve güvenilir implementasyon
 */

import Swal from 'sweetalert2';

export interface PinPromptResult {
  pin: string;
  remember: boolean;
}

export interface PinPromptOptions {
  ownerName: string;
  title?: string;
  rememberLabel?: string;
  minLen?: number;
  maxLen?: number;
}

/**
 * Kullanıcıdan PIN kodu ister
 */
export async function promptForPin(opts: PinPromptOptions): Promise<PinPromptResult | null> {
  const {
    ownerName,
    title = 'İmza Pin Kodu',
    rememberLabel = 'Otomatik oturum aç',
    minLen = 4,
    maxLen = 12
  } = opts;

  const html = `
    <div style="text-align: left; margin-top: 10px; font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, 'Helvetica Neue', Arial, sans-serif;">
      <div style="margin-bottom: 20px; padding: 14px; background: #f8f9fa; border-radius: 8px; border: 1px solid #e9ecef;">
        <div style="color: #495057; font-size: 16px; font-weight: 600; margin-bottom: 10px; letter-spacing: 0.3px;">
          Seçili İmza Sahibi:
        </div>
        <div style="color: #212529; font-size: 17px; font-weight: 500; line-height: 1.5; letter-spacing: 0.2px;">
          ${ownerName}
        </div>
      </div>

      <input
        id="pinInput"
        type="password"
        class="swal2-input"
        placeholder="PIN Kodunuzu girin"
        style="margin: 10px 0; width: 90%; font-size: 17px; font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, 'Helvetica Neue', Arial, sans-serif; letter-spacing: 0.5px;"
        inputmode="numeric"
        pattern="[0-9]*"
      />

      <div style="margin-top: 18px;">
        <label style="display: flex; align-items: center; cursor: pointer; font-size: 17px; font-weight: 500; letter-spacing: 0.2px;">
          <!-- Varsayılan işaretli: kutu artık gerçekten bir davranışı kontrol ediyor.
               İşaretliyken kart takıldığında oturum kendiliğinden açılıyor, PIN bir
               daha sorulmuyor. İşaretsizken otomatik giriş denenmez. -->
          <input type="checkbox" id="rememberMe" checked style="margin-right: 10px; width: 20px; height: 20px; cursor: pointer;">
          <span>${rememberLabel}</span>
        </label>
      </div>
    </div>
  `;

  const result = await Swal.fire({
    title: `🔒 ${title}`,
    html: html,
    showCancelButton: true,
    confirmButtonText: 'Devam Et',
    cancelButtonText: 'Vazgeç',
    allowOutsideClick: false,
    allowEscapeKey: true,
    reverseButtons: true,
    customClass: {
      confirmButton: 'swal2-confirm-custom',
      cancelButton: 'swal2-cancel-custom'
    },
    buttonsStyling: false,
    didOpen: () => {
      // Custom stil ekle
      const style = document.createElement('style');
      style.textContent = `
        .swal2-confirm-custom {
          background-color: #10b981 !important;
          color: white !important;
          border: none !important;
          padding: 12px 28px !important;
          font-size: 16px !important;
          font-weight: 600 !important;
          border-radius: 8px !important;
          cursor: pointer !important;
          transition: all 0.2s ease !important;
          margin: 0 8px !important;
        }
        .swal2-confirm-custom:hover {
          background-color: #059669 !important;
          transform: translateY(-1px);
          box-shadow: 0 4px 12px rgba(16, 185, 129, 0.3) !important;
        }
        .swal2-cancel-custom {
          background-color: #6b7280 !important;
          color: white !important;
          border: none !important;
          padding: 12px 28px !important;
          font-size: 16px !important;
          font-weight: 600 !important;
          border-radius: 8px !important;
          cursor: pointer !important;
          transition: all 0.2s ease !important;
          margin: 0 8px !important;
        }
        .swal2-cancel-custom:hover {
          background-color: #4b5563 !important;
          transform: translateY(-1px);
          box-shadow: 0 4px 12px rgba(107, 114, 128, 0.3) !important;
        }
        .swal2-actions {
          margin-top: 20px !important;
          gap: 0 !important;
        }
      `;
      document.head.appendChild(style);

      const input = document.getElementById('pinInput') as HTMLInputElement;
      if (input) {
        input.focus();
        input.addEventListener('keypress', (e) => {
          if (e.key === 'Enter') {
            Swal.clickConfirm();
          }
        });
      }
    },
    preConfirm: () => {
      const pin = (document.getElementById('pinInput') as HTMLInputElement)?.value?.trim();
      const remember = (document.getElementById('rememberMe') as HTMLInputElement)?.checked;

      if (!pin) {
        Swal.showValidationMessage('PIN kodunu giriniz');
        return false;
      }

      if (pin.length < minLen || pin.length > maxLen) {
        Swal.showValidationMessage(`PIN uzunluğu ${minLen}-${maxLen} karakter olmalı`);
        return false;
      }

      if (!/^\d+$/.test(pin)) {
        Swal.showValidationMessage('PIN yalnızca rakamlardan oluşmalı');
        return false;
      }

      return { pin, remember };
    }
  });

  if (result.isConfirmed && result.value) {
    return result.value;
  }

  return null;
}

/**
 * Basit PIN isteme (geriye dönük uyumluluk için)
 */
export async function promptForPinSimple(ownerInfo: string): Promise<string | null> {
  const result = await promptForPin({
    ownerName: ownerInfo,
    title: 'İmza Pin Kodu',
    minLen: 4,
    maxLen: 12
  });

  return result ? result.pin : null;
}
